import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { User, getCurrentUser } from '../services/auth';
import { setMustChangePasswordHandler } from '../services/api';
import {
  getToken,
  tokenRequiresPasswordChange,
  getAuthScope,
} from '../utils/authStorage';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  mustChangePassword: boolean;
  setMustChangePassword: (v: boolean) => void;
  refreshUser: () => Promise<void>;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  mustChangePassword: false,
  setMustChangePassword: () => {},
  refreshUser: async () => {},
  setUser: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const refreshUser = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) {
        setUser(null);
        setMustChangePassword(false);
        return;
      }
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      const fromToken = tokenRequiresPasswordChange(token);
      const fromUser = !!(currentUser as any)?.mustChangePassword;
      setMustChangePassword(fromToken || fromUser);
    } catch (error) {
      console.error('AuthContext: refreshUser error', error);
    }
  }, []);

  useEffect(() => {
    setMustChangePasswordHandler(() => setMustChangePassword(true));
    refreshUser().finally(() => setLoading(false));
    return () => setMustChangePasswordHandler(null);
  }, [refreshUser]);

  // 路由变化时按作用域刷新（前台/管理端 token 不同）
  useEffect(() => {
    const onPop = () => {
      void refreshUser();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [refreshUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        mustChangePassword,
        setMustChangePassword,
        refreshUser,
        setUser: (u) => {
          setUser(u);
          if (u) {
            const token = getToken(getAuthScope());
            setMustChangePassword(tokenRequiresPasswordChange(token) || !!(u as any).mustChangePassword);
          } else {
            setMustChangePassword(false);
          }
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
